import { useEffect, useRef } from 'react';

const experiences = [
  {
    icon: 'ri-drinks-2-line',
    title: 'Welcome Experience',
    description: 'Recepção exclusiva, credenciamento premium, welcome brunch e um momento pensado para que você se sinta acolhida desde o primeiro instante.',
    accent: 'oklch(var(--primary-500))',
  },
  {
    icon: 'ri-chat-3-line',
    title: 'Talks & Insights',
    description: 'Conversas profundas com especialistas sobre planejamento consciente, tendências reais, orçamento inteligente e criação de experiências autênticas.',
    accent: 'oklch(var(--accent-500))',
  },
  {
    icon: 'ri-links-line',
    title: 'Curadoria e Conexões',
    description: 'Espaço cuidadosamente desenhado para conexões genuínas com marcas e profissionais selecionados que compartilham da mesma filosofia.',
    accent: 'oklch(var(--primary-500))',
  },
  {
    icon: 'ri-star-line',
    title: 'Future Talk',
    description: 'Painel exclusivo com convidados especiais que vão compartilhar visões, histórias e perspectivas que vão transformar a forma como você enxerga o seu dia.',
    accent: 'oklch(var(--accent-500))',
  },
  {
    icon: 'ri-gift-line',
    title: 'Experience Kit',
    description: 'Uma curadoria especial de materiais e surpresas desenvolvidas para acompanhar sua jornada muito além do dia do evento.',
    accent: 'oklch(var(--primary-500))',
  },
];

export default function ExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="experiencia" className="relative py-24 md:py-36 px-6 md:px-10" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 md:mb-24 reveal-on-scroll">
          <span
            className="inline-block text-[10px] tracking-[0.3em] uppercase px-4 py-2 rounded-full border mb-6"
            style={{
              color: 'oklch(var(--secondary-500))',
              borderColor: 'oklch(var(--secondary-300) / 0.5)',
            }}
          >
            A Experiência
          </span>
          <h2
            className="font-heading text-3xl md:text-5xl lg:text-6xl leading-[1.12] mb-6"
            style={{ color: 'oklch(var(--foreground-900))' }}
          >
            O que você vai
            <br />
            vivenciar
          </h2>
          <p className="text-sm md:text-base max-w-xl mx-auto leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
            Cada módulo foi pensado para oferecer uma experiência transformadora e
            profundamente conectada com a sua realidade.
          </p>
        </div>

        {/* Experience Grid - 2 columns for first 4, center the 5th */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {experiences.slice(0, 4).map((exp, index) => (
            <div
              key={exp.title}
              className="reveal-on-scroll group p-8 md:p-10 rounded-lg transition-all duration-500 cursor-default relative overflow-hidden"
              style={{
                backgroundColor: 'oklch(var(--background-50))',
                border: '1px solid oklch(var(--background-200) / 0.6)',
                transitionDelay: `${index * 0.1}s`,
              }}
            >
              <div
                className="absolute top-0 left-0 w-1 h-full transition-all duration-500 opacity-0 group-hover:opacity-100"
                style={{ backgroundColor: exp.accent }}
              ></div>

              <div className="flex items-start gap-5">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110"
                  style={{ backgroundColor: 'oklch(var(--background-100))' }}
                >
                  <i
                    className={`${exp.icon} text-xl`}
                    style={{ color: exp.accent }}
                  ></i>
                </div>
                <div>
                  <h3
                    className="font-heading text-xl md:text-2xl mb-3 transition-colors duration-500"
                    style={{ color: 'oklch(var(--foreground-900))' }}
                  >
                    {exp.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
                    {exp.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 5th card - centered */}
        <div className="mt-6 md:mt-8 max-w-2xl mx-auto">
          {(() => {
            const exp = experiences[4];
            return (
              <div
                className="reveal-on-scroll group p-8 md:p-10 rounded-lg transition-all duration-500 cursor-default relative overflow-hidden"
                style={{
                  backgroundColor: 'oklch(var(--background-50))',
                  border: '1px solid oklch(var(--background-200) / 0.6)',
                  transitionDelay: '0.4s',
                }}
              >
                <div
                  className="absolute top-0 left-0 w-1 h-full transition-all duration-500 opacity-0 group-hover:opacity-100"
                  style={{ backgroundColor: exp.accent }}
                ></div>

                <div className="flex items-start gap-5">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110"
                    style={{ backgroundColor: 'oklch(var(--background-100))' }}
                  >
                    <i
                      className={`${exp.icon} text-xl`}
                      style={{ color: exp.accent }}
                    ></i>
                  </div>
                  <div>
                    <h3
                      className="font-heading text-xl md:text-2xl mb-3 transition-colors duration-500"
                      style={{ color: 'oklch(var(--foreground-900))' }}
                    >
                      {exp.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
                      {exp.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Bottom decorative quote */}
        <div className="mt-20 md:mt-28 text-center reveal-on-scroll">
          <div className="divider-accent max-w-md mx-auto mb-8"></div>
          <p
            className="font-heading italic text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed"
            style={{ color: 'oklch(var(--foreground-700))' }}
          >
            "Cada detalhe foi pensado para que você se sinta a protagonista
            absoluta da sua própria história."
          </p>
        </div>
      </div>
    </section>
  );
}